import React, { useState } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  makeStyles,
  SvgIcon,
  TextField,
  Card,
  CircularProgress,
  withStyles
} from '@material-ui/core';
import Slide from '@material-ui/core/Slide';
import Autocomplete from '@material-ui/lab/Autocomplete';
import clsx from 'clsx';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Search as SearchIcon } from 'react-feather';
import { ArrowRight, ArrowLeft } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { DatePicker } from 'antd';
import styled from 'styled-components';

const StyledDatePicker = styled(DatePicker)`
  transition: border-color 0.3s ease;

  &:hover {
    border-color: #ee0434;
  }

  &.ant-picker-focused {
    border-color: #ee0434;
  }

  .ant-picker-input > input {
    height: 100%;
    line-height: 40px;
  }
`;

const HeaderToolBar = ({
  className,
  querySearch,
  searchRef,
  clearSearchRef,
  createNewDeviceRef,
  isLoading,
  setShowResult,
  toggleSidebar,
  isSidebarOpen,
  ...rest
}) => {
  const classes = useStyles({ isSidebarOpen });

  const formatDate = date => moment(date).format('YYYY-MM-DD');
  const formatTime = date => moment(date).format('HH:mm');
  const formatDateTime = (date, time) => {
    if (!date || !time) return '';
    return moment(`${formatDate(date)} ${time}`, 'YYYY-MM-DD HH:mm').format(
      'YYYY-MM-DDTHH:mm'
    );
  };

  const [query, setQuery] = useState({
    license_plate: querySearch.name || '',
    date: querySearch.date || moment(),
    start_time: querySearch.start_time || '',
    end_time: querySearch.end_time || '',
    camera: querySearch.camera || 'Chọn camera'
  });

  const [selectedHours, setSelectedHours] = useState(null);
  const [showCustomTime, setShowCustomTime] = useState(false);

  const listLicensePlateCamera = useSelector(
    state => state.vehicleSlice.listLicensePlateCamera
  );

  const cameraOptions = [
    { label: 'Chọn camera', value: 'Chọn camera' },
    { label: 'Camera 1', value: '1' },
    { label: 'Camera 2', value: '2' },
    { label: 'Camera 3', value: '3' },
    { label: 'Camera 4', value: '4' }
  ];

  const isFormValid = () => {
    return (
      query.license_plate && query.date && query.start_time && query.end_time
    );
  };

  const onEnterSearchInput = event => {
    if (!searchRef || !query?.license_plate) return;
    if (event.keyCode === 13) {
      const queryValue = { ...query, page: 1 };
      setQuery(queryValue);
      const formattedQuery = {
        license_plate: queryValue.license_plate,
        start_date: formatDateTime(queryValue.date, queryValue.start_time),
        end_date: formatDateTime(queryValue.date, queryValue.end_time),
        camera: queryValue.camera === 'Chọn camera' ? 'all' : queryValue.camera,
        page: queryValue.page,
        page_size: 10000
      };
      if (
        formattedQuery.start_date &&
        formattedQuery.end_date &&
        isFormValid()
      ) {
        searchRef(formattedQuery);
        setShowResult(true);
        setSelectedHours(null);
      }
    }
  };

  const changeTextInputSearch = (event, value) => {
    const newValue = value || event?.target?.value || '';
    const queryValue = { ...query, license_plate: newValue, page: 1 };
    setQuery(queryValue);
  };

  const changeDate = (value) => {
    const queryValue = {
      ...query,
      date: value,
      page: 1
    };
    setQuery(queryValue);
    setSelectedHours(null);
  };

  const changeStartTime = event => {
    const queryValue = {
      ...query,
      start_time: event.target.value,
      page: 1
    };
    setQuery(queryValue);
    setSelectedHours('Khác');
  };

  const changeEndTime = event => {
    const queryValue = {
      ...query,
      end_time: event.target.value,
      page: 1
    };
    setQuery(queryValue);
    setSelectedHours('Khác');
  };

  const changeCamera = (event, value) => {
    const selectedValue = value ? value.value : 'Chọn camera';
    const queryValue = { ...query, camera: selectedValue, page: 1 };
    setQuery(queryValue);
  };

  const handleQuickTimeSelect = (hours, time) => {
    let queryValue = { ...query, page: 1 };
    const selectedDate = query.date
      ? moment(query.date, 'YYYY-MM-DD')
      : moment().startOf('day');
    const startOfSelectedDay = selectedDate.clone().startOf('day');
    const now = moment();
    const isToday = selectedDate.isSame(moment(), 'day');

    if (time === 'Khác') {
      queryValue.start_time = '00:00';
      queryValue.end_time = isToday ? formatTime(now) : '23:59';
      setShowCustomTime(true);
      setSelectedHours('Khác');
    } else {
      if (isToday) {
        queryValue.end_time = formatTime(now);
        const startTime = now.clone().subtract(hours, 'hours');
        queryValue.start_time = startTime.isBefore(startOfSelectedDay)
          ? '00:00'
          : formatTime(startTime);
      } else {
        queryValue.start_time = '00:00';
        queryValue.end_time =
          time === '24H'
            ? '23:59'
            : formatTime(startOfSelectedDay.clone().add(hours, 'hours'));
      }
      setShowCustomTime(false);
      setSelectedHours(time);
    }
    if (selectedDate.isAfter(moment(), 'day')) {
      queryValue.end_time = formatTime(now);
    }

    setQuery(queryValue);
  };

  const searchDevice = () => {
    if (!searchRef || !query.license_plate || !isFormValid()) {
      return;
    }
    const formattedQuery = {
      license_plate: query.license_plate,
      start_date: formatDateTime(query.date, query.start_time),
      end_date: formatDateTime(query.date, query.end_time),
      camera: query.camera === 'Chọn camera' ? 'all' : query.camera,
      page: query.page || 1,
      page_size: 10000
    };
    if (formattedQuery.start_date && formattedQuery.end_date) {
      searchRef(formattedQuery);
      setShowResult(true);
    }
  };

  const onOk = value => {};

  return (
    <Box className={clsx(classes.root, className)} {...rest}>
      <Slide direction="right" in={isSidebarOpen}>
        <Card className={classes.sidebar}>
          <Box className={classes.sidebarContent}>
            <div
              className={classes.labelChooseTime}
              style={{ marginBottom: '22px' }}
            >
              Chọn phương tiện và camera
            </div>
            <Box className={classes.fieldWrapper} display="flex" gap={8}>
              <Autocomplete
                freeSolo
                id="free-solo-2-demo"
                size="small"
                disableClearable
                options={listLicensePlateCamera}
                inputValue={query?.license_plate}
                onChange={changeTextInputSearch}
                classes={{ option: classes.option }}
                renderInput={params => (
                  <CssTextField
                    {...params}
                    required={true}
                    className={classes.searchInput}
                    InputProps={{
                      ...params.InputProps,
                      style: {
                        fontSize: '14px'
                      },
                      startAdornment: (
                        <InputAdornment position="start">
                          <SvgIcon fontSize="small" color="action">
                            <SearchIcon />
                          </SvgIcon>
                        </InputAdornment>
                      )
                    }}
                    variant="outlined"
                    placeholder="Nhập phương tiện"
                    onKeyDown={onEnterSearchInput}
                    onChange={changeTextInputSearch}
                  />
                )}
                style={{ flex: 1 }}
              />
              <Autocomplete
                id="camera-filter"
                size="small"
                options={cameraOptions}
                getOptionLabel={option => option.label}
                value={
                  cameraOptions.find(
                    option => option.value === query.camera
                  ) || { label: 'Chọn camera', value: 'Chọn camera' }
                }
                onChange={changeCamera}
                disableClearable
                renderInput={params => (
                  <CssTextField
                    {...params}
                    variant="outlined"
                    placeholder="Chọn camera"
                    InputProps={{
                      ...params.InputProps,
                      style: { fontSize: '14px' }
                    }}
                  />
                )}
                style={{ flex: 1 }}
              />
            </Box>

            <Box className={classes.fieldWrapper}>
              <div
                className={classes.labelChooseTime}
                style={{ marginBottom: '10px' }}
              >
                Chọn ngày và khoảng thời gian
              </div>
              <StyledDatePicker
                format={'DD-MM-YYYY'}
                value={query.date}
                onChange={changeDate}
                onOk={onOk}
                placeholder='Chọn thời gian'
                style={{
                  width: '352px',
                  height: '40px',
                  fontSize: '16px',
                  borderRadius: '4px'
                }}
              />
            </Box>

            <Box className={classes.fieldWrapper}>
              <Box
                display="grid"
                gridTemplateColumns="repeat(7, 1fr)"
                gap={8}
                width="100%"
              >
                {['1H', '4H', '8H', '12H', '24H', 'Khác'].map((time, index) => {
                  const hours = time === 'Khác' ? 0 : parseInt(time);
                  const isSelected = selectedHours === time;
                  return (
                    <Button
                      fullWidth
                      key={index}
                      variant="outlined"
                      size="small"
                      onClick={() => handleQuickTimeSelect(hours, time)}
                      style={{
                        fontSize: '14px',
                        padding: '4px 8px',
                        minWidth: '58px',
                        borderLeft: '0.5px solid #EBECED',
                        borderRight: '0.5px solid #EBECED',
                        borderTop: 'none',
                        borderBottom: 'none',
                        color: isSelected ? '#fff' : '#858C93',
                        backgroundColor: isSelected ? '#D9042F' : '#F5FAFF'
                      }}
                    >
                      {time}
                    </Button>
                  );
                })}
              </Box>
            </Box>

            {showCustomTime && (
              <Box className={classes.fieldWrapper}>
                <div className={classes.labelTime}>Từ giờ</div>
                <CssTextField
                  size="small"
                  fullWidth
                  margin="normal"
                  type="time"
                  name="start_time"
                  value={query.start_time}
                  onChange={changeStartTime}
                  variant="outlined"
                  InputProps={{
                    style: {
                      fontSize: '14px',
                      fontWeight: '400',
                      lineHeight: '140%',
                      color: '#34404B',
                      fontFamily: 'iCiel Helvetica Now Display'
                    }
                  }}
                />
                <div className={classes.labelTime}>Đến giờ</div>
                <CssTextField
                  size="small"
                  fullWidth
                  margin="normal"
                  type="time"
                  name="end_time"
                  value={query.end_time}
                  onChange={changeEndTime}
                  variant="outlined"
                  InputProps={{
                    style: {
                      fontSize: '14px',
                      fontWeight: '400',
                      lineHeight: '140%',
                      color: '#34404B',
                      fontFamily: 'iCiel Helvetica Now Display'
                    }
                  }}
                />
              </Box>
            )}

            <Box className={classes.fieldWrapper} position="relative">
              <Button
                fullWidth
                style={{
                  height: '48px',
                  background:
                    isLoading || !isFormValid() ? '#E0E0E0' : '#D9042F',
                  color: isLoading || !isFormValid() ? '#A0A5AA' : '#fff',
                  textTransform: 'inherit',
                  fontSize: '16px',
                  lineHeight: '20px',
                  fontFamily: 'iCiel Helvetica Now Display',
                  fontWeight: '500'
                }}
                disabled={!isFormValid() || isLoading}
                variant="contained"
                onClick={searchDevice}
              >
                {isLoading ? (
                  <>
                    <CircularProgress
                      size={24}
                      style={{ color: '#A0A5AA', marginRight: '8px' }}
                    />
                    <span>Vui lòng đợi ...</span>
                  </>
                ) : (
                  <span>Tìm kiếm ảnh</span>
                )}
              </Button>
            </Box>
          </Box>
        </Card>
      </Slide>

      <Box
        className={classes.toggleButton}
        style={{ display: isSidebarOpen ? 'block' : 'none' }}
      >
        <Box
          title="Ẩn Menu"
          onClick={toggleSidebar}
          className={classes.centeredBox}
        >
          <ArrowLeft />
        </Box>
      </Box>

      <Box
        className={classes.showMenuButton}
        style={{ display: isSidebarOpen ? 'none' : 'block' }}
      >
        <Box
          title="Hiện Menu"
          onClick={toggleSidebar}
          className={classes.centeredBox}
        >
          <ArrowRight />
        </Box>
      </Box>
    </Box>
  );
};

HeaderToolBar.propTypes = {
  className: PropTypes.string,
  searchRef: PropTypes.func,
  clearSearchRef: PropTypes.func,
  createNewDeviceRef: PropTypes.func,
  isLoading: PropTypes.bool,
  setShowResult: PropTypes.func,
  toggleSidebar: PropTypes.func,
  isSidebarOpen: PropTypes.bool,
  querySearch: PropTypes.object
};

const useStyles = makeStyles(theme => ({
  root: {
    position: 'fixed',
    top: 0,
    left: '0px',
    height: '100vh',
    zIndex: 1000
  },
  sidebar: {
    width: props => (props.isSidebarOpen ? '384px' : '0'),
    overflow: props => (props.isSidebarOpen ? 'auto' : 'hidden'),
    height: '100%',
    backgroundColor: '#FFFFFF',
    boxShadow: '5px 5px 15px 0px #9e9e9ede',
    paddingTop: '50px',
    padding: props => (props.isSidebarOpen ? '66px 0px' : '0')
  },
  sidebarContent: {
    padding: theme.spacing(2)
  },
  toggleButton: {
    position: 'absolute',
    top: '70px',
    left: props => (props.isSidebarOpen ? '384px' : '0px'),
    zIndex: 999,
    backgroundColor: '#ffffff',
    borderRadius: '0 8px 8px 0',
    boxShadow: 'rgb(220,220,220) 3px 3px 6px 0px inset',
    width: '28px',
    height: '48px',
    cursor: 'pointer'
  },
  showMenuButton: {
    position: 'absolute',
    top: '70px',
    left: '0px',
    zIndex: 999,
    backgroundColor: '#ffffff',
    borderRadius: '0 8px 8px 0',
    boxShadow: 'rgb(220,220,220) 3px 3px 6px 0px inset',
    width: '28px',
    height: '48px',
    cursor: 'pointer'
  },
  fieldWrapper: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    gap: theme.spacing(1)
  },
  label: {
    marginBottom: '10px',
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: 'iCiel Helvetica Now Display',
    color: '#0C1132'
  },
  labelTime: {
    lineHeight: '140%',
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: 'iCiel Helvetica Now Display',
    color: '#0C1132'
  },
  labelChooseTime: {
    fontSize: '16px',
    fontWeight: '700',
    lineHeight: '20px',
    color: '#0C1132'
    //fontFamily: 'iCiel Helvetica Now Display',
  },
  centeredBox: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  option: {
    fontSize: '14px'
  }
}));

const CssTextField = withStyles({
  root: {
    '& .MuiInput-underline:after': {
      borderBottomColor: '#D6D9DB'
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#D6D9DB'
      },
      '&:hover fieldset': {
        borderColor: '#D9042F'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#D9042F'
      }
    }
  }
})(TextField);

export default HeaderToolBar;
